import { DatePicker } from "@rio-cloud/rio-uikit";
import moment, { Moment } from "moment";
import { AsyncPaginate } from "react-select-async-paginate";
import type { GroupBase } from 'react-select';
import { FormFields, IPagedSelectOptionItens } from "../../../../models/newSupportCall";
import useNewSupportCall from "../../../../hooks/useNewSupportCall";
import { formatPhoneNumber } from "../../../../utils/phoneUtils";
import { optionsTechnical } from "../../../support/constants";
import SelectCustom from "../../../../components/selectCustom";

export interface IAdministrativeFormData {
    formData: FormFields;
    errors: Partial<Record<keyof FormFields, string>>;
    updateField: <K extends keyof FormFields>(field: K, value: FormFields[K]) => void;
    name: string;
}

const TechnicalForm = ({ formData, errors, updateField, name }: IAdministrativeFormData) => {

    const { 
        getSupportRegionals,
        selectedRegional,
        setSelectedRegional,
        dnOptions,
        getAllDns,
        role
    } = useNewSupportCall();

    const updateResponsible = (value: string) => updateField('responsible', value);
    const updateChassis = (value: string) => updateField('chassis', value);
    const updateLicensePlate = (value: string) => updateField('licensePlate', value);
    const updateDn = (value: string) => updateField('dn', value);
    const updateDnResponsible = (value: string) => updateField('dnResponsible', value);
    const updatePhone = (value: string) => updateField('phone', value);
    const updateCustomer = (value: string) => updateField('customer', value);
    const updateHourMeter = (value: string) => updateField('hourMeter', value);
    const updateOdometer = (value: string) => updateField('odometer', value);
    const updatePartName = (value: string) => updateField('partName', value);
    const updateAeg = (value: string) => updateField('aeg', value);
    const updateEntryDate = (value: Moment | string) => updateField('entryDate', value);
    const updateRegionalId = (value: string) => updateField('regionalId', value);
    const updateCustomerComment = (value: string) => updateField('customerComment', value);
    const updateDiagnose = (value: string) => updateField('diagnose', value);
    const updateRepair = (value: string) => updateField('repair', value);
    const updateAnalysisReason = (value: string) => updateField('analysisReason', value);
    const updateSubtopic = (value: string) => updateField('subtopic', value);

    return (
        <>
            <div className='padding-top-15 padding-x-15 rounded border'>
                <div className=''>
                    <p className='text-size-14 text-color-darker'>Tema</p>
                    <div className='form-group display-flex padding-top-10'>
                        <select
                            className='form-control'
                            value={formData.subtopic}
                            onChange={event => updateSubtopic(event.target.value)}
                        >
                            {optionsTechnical.map(option => {
                                return (
                                    <option value={option.id}>{option.label}</option>
                                )
                            })}
                        </select>
                    </div>
                </div>
                <div className='display-flex justify-between flex-wrap gap-20'>
                    <div className='form-group flex-1-1'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Responsável*
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={name || formData.responsible}
							disabled={Boolean(name?.trim())}
                            onChange={event => updateResponsible(event.target.value)}
                        />
                        {errors.responsible && (
                            <span className='text-size-12 text-color-danger'>{errors.responsible}</span>
                        )}
                    </div>
                    <div className='form-group flex-1-1'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>Dn*</label>
                        {role === 'GROUP_CONSULTANT' || role === 'CONSULTANT' ? (
							<select
								className='form-control height-35'
								value={formData.dn}
								onChange={event => updateDn(event.target.value)}
							>
								<option value=''>Selecione</option>
								{dnOptions?.map(dnItem => {
									return (
										<option value={dnItem.value}>{dnItem.label}</option>
									)
								})}
							</select>
						) : null}

                        {role === 'TOWER' || role === 'FACTORY' ? (
							<SelectCustom
								value={[formData.dn || '']}
								onChange={(item) => {updateDn(item?.id || '')}}
								loadData={getAllDns}
								disabled={false}
								isRemoved={false}
								loadOnFocus={true}
								openOnFocus={false}
								autoFocus={false}
								initialLimit={10}
								placeholder="Digite para buscar opcões"
								inputBorderColor="#d0d8de"
								inputBackgroundColor="#ffffff"
								dropdownBackgroundColor="#ffffff"
								highlightedOptionColor="#3b82f6"
								selectedOptionColor="#e5e7eb" 
							/>
						) : null}

                        {errors.dn && (
                            <span className='text-size-12 text-color-danger'>{errors.dn}</span>
                        )}
                    </div>
                    <div className='form-group flex-1-1 margin-x-10'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Chassi do veículo *
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.chassis}
                            onChange={event => updateChassis(event.target.value)}
                    		maxLength={17}
                        />
                        {errors.chassis && (
                            <span className='text-size-12 text-color-danger'>{errors.chassis}</span>
                        )}
                    </div>
                    <div className='form-group flex-1-1'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Placa do veículo *
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.licensePlate}
                            onChange={event => updateLicensePlate(event.target.value)}
                        />
                        {errors.licensePlate && (
                            <span className='text-size-12 text-color-danger'>{errors.licensePlate}</span>
                        )}
                    </div>
                </div>
                <div className='display-flex justify-between flex-wrap gap-20'>
                    <div className='form-group flex-1-1 max-width-350'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Responsável pelo chamado no DN*
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.dnResponsible}
                            onChange={event => updateDnResponsible(event.target.value)}
                        />
                        {errors.dnResponsible && (
                            <span className='text-size-12 text-color-danger'>{errors.dnResponsible}</span>
                        )}
                    </div>
                    <div className='form-group flex-1-1 margin-x-10'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Tel para contato*
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.phone}
                            onChange={event => { 
                                const value = formatPhoneNumber(event.target.value)
                                updatePhone(value)
                            }}
                        />
                        {errors.phone && (
                            <span className='text-size-12 text-color-danger'>{errors.phone}</span>
                        )}
                    </div>
                    <div className='form-group flex-1-1 max-width-350'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Nome do cliente *
                        </label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.customer}
                            onChange={event => updateCustomer(event.target.value)}
                        />
                        {errors.customer && (
							<span className='text-size-12 text-color-danger'>{errors.customer}</span>
						)}
                    </div>
                </div>
                <div className='display-flex justify-between flex-wrap gap-20'>
                    <div className='form-group margin-0 flex-1-1 max-width-350'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>Horímetro *</label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.hourMeter}
                            onChange={event => updateHourMeter(event.target.value)}
                        />
                        {errors.hourMeter && (
                            <span className='text-size-12 text-color-danger'>{errors.hourMeter}</span>
                        )}
                    </div>
                    <div className='form-group margin-0 flex-1-1 margin-x-10'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>Km*</label>
                        <input
                            type='text'
                            className='form-control'
                            value={formData.odometer}
                            onChange={event => updateOdometer(event.target.value)}
                        />
                        {errors.odometer && (
                            <span className='text-size-12 text-color-danger'>{errors.odometer}</span>
                        )}
                    </div>
                    <div className='form-group margin-0 flex-1-1 max-width-350'>
                        <label className='text-size-14 text-color-darker margin-bottom-10'>
                            Data de entrada do veículo*
                        </label>
                        <DatePicker
                            id='tech'
                            inputProps={{ placeholder: 'Selecione' }}
                            dateFormat='DD/MM/YYYY'
                            timeFormat={false}
                            onChange={(result: Moment | string) => updateEntryDate(result)}
                            isValidDate={(currentDate: Moment) => currentDate.isSameOrBefore(moment(), 'day')}
                            className="margin-0"
                        />
                        {errors.entryDate && (
                            <span className='text-size-12 text-color-danger'>{errors.entryDate}</span>
                        )}
                    </div>
                </div>
                <div className='form-group flex-1-1'>
                    <div>
                        <p className='text-size-14 text-color-darker margin-0'>Regional</p>
                        <div className='form-group margin-0 display-flex padding-top-10'>
                            <AsyncPaginate<IPagedSelectOptionItens, GroupBase<IPagedSelectOptionItens>, unknown>
                                className='w-full'
                                value={selectedRegional}
                                loadOptions={getSupportRegionals}
                                onChange={value => {
                                    const valueSelected = value?.value ? String(value?.value) : '';

                                        setSelectedRegional(value);
                                        updateRegionalId(valueSelected);
                                }}
                                placeholder='Selecione a regional'
                            />
                        </div>
                        {errors.regionalId && (
                            <span className='text-size-12 text-color-danger'>{errors.regionalId}</span>
                        )}
                    </div>
                </div>
            </div>
            <div className='margin-top-20'>
                <p className='text-size-16 text-bold'>2. Dados adicionais</p>
                    <div className='padding-15 margin-top-20 rounded border'>
                        <div className='display-flex justify-between flex-wrap gap-20'>
                            <div className='form-group flex-1-1 margin-right-10'>
                                <label className='text-size-14 text-color-darker margin-bottom-10'>Nome da peça causadora *</label>
                                <input
                                    type='text'
                                    className='form-control height-35'
                                    value={formData.partName}
                                    onChange={event => updatePartName(event.target.value)}
                                />
                                {errors.partName && (
                                    <span className='text-size-12 text-color-danger'>{errors.partName}</span>
                                )}
                            </div>
                            <div className='form-group flex-1-1'>
                                <label className='text-size-14 text-color-darker margin-bottom-10'>Nº AEG</label>
                                <input
                                    type='text'
                                    className='form-control height-35'
                                    value={formData.aeg}
                                    onChange={event => updateAeg(event.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <p className='text-size-14 text-color-darker'>Qual a reclamação do cliente? *</p>
                            <textarea 
                                className="form-control margin-top-10" 
                                rows={3} 
                                id="textArea" 
                                value={formData.customerComment}
                                onChange={event => updateCustomerComment(event.target.value)}
                            ></textarea>
                            {errors.customerComment && (
                                <span className='text-size-12 text-color-danger'>{errors.customerComment}</span>
                            )}
                        </div>
                        <div className="margin-top-20">
                            <p className='text-size-14 text-color-darker'>Diagnóstico / causa da falha *</p>
                            <textarea 
                                className="form-control margin-top-10" 
                                rows={3} 
                                id="textArea" 
                                value={formData.diagnose}
                                onChange={event => updateDiagnose(event.target.value)}
                            ></textarea>
                            {errors.diagnose && (
                                <span className='text-size-12 text-color-danger'>{errors.diagnose}</span>
                            )}
                        </div>
                        <div className="margin-top-20">
                            <p className='text-size-14 text-color-darker'>Reparo necessário *</p>
                            <textarea 
                                className="form-control margin-top-10" 
                                rows={3} 
                                id="textArea" 
                                value={formData.repair}
                                onChange={event => updateRepair(event.target.value)}
                            ></textarea>
                            {errors.repair && (
                                <span className='text-size-12 text-color-danger'>{errors.repair}</span>
                            )}
                        </div>
                        <div className="margin-top-20">
                            <p className='text-size-14 text-color-darker'>Motivo do envio para análise *</p>
                            <textarea 
                                className="form-control margin-top-10" 
                                rows={3} 
                                id="textArea" 
                                value={formData.analysisReason}
                                onChange={event => updateAnalysisReason(event.target.value)}
                            ></textarea>
                            {errors.analysisReason && (
                                <span className='text-size-12 text-color-danger'>{errors.analysisReason}</span>
                            )}
                        </div>
                    </div>
            </div>
        </>
    )
}

export default TechnicalForm;